import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ToastContainer, ToastContent } from "./styles";
import { useReducedMotion } from "../../hooks/useReducedMotion";

export interface KLReturningOwnerToastProps {
  /** Controls whether the toast is currently visible. */
  show: boolean;
  /** Called after the exit animation completes. */
  onDismiss: () => void;
  /** Auto-dismiss delay in milliseconds. Defaults to 6000. */
  autoDismissDuration?: number;
}

const EXIT_ANIMATION_DURATION = 300; // ms
const RETURNING_OWNER_MESSAGE =
  "המשכנו מהמקום שעצרת — הפרטים שמילאת קודם נשמרו.";

const KLReturningOwnerToast: React.FC<KLReturningOwnerToastProps> = ({
  show,
  onDismiss,
  autoDismissDuration = 6000,
}) => {
  const reducedMotion = useReducedMotion();
  const [isVisible, setIsVisible] = useState(false);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();

  useEffect(() => {
    if (!show) return;
    setIsVisible(true);

    const autoTimer = setTimeout(() => {
      setIsVisible(false);
      dismissTimerRef.current = setTimeout(
        onDismiss,
        EXIT_ANIMATION_DURATION,
      );
    }, autoDismissDuration);

    return () => {
      clearTimeout(autoTimer);
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [show, autoDismissDuration, onDismiss]);

  const handleTap = useCallback(() => {
    setIsVisible(false);
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = setTimeout(onDismiss, EXIT_ANIMATION_DURATION);
  }, [onDismiss]);

  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, []);

  return (
    <AnimatePresence>
      {show && isVisible && (
        <ToastContainer
          role="status"
          aria-live="polite"
          aria-atomic="true"
          onClick={handleTap}
          initial={
            reducedMotion ? { opacity: 0 } : { opacity: 0, y: -30 }
          }
          animate={
            reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }
          }
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : { type: "spring", stiffness: 260, damping: 24 }
          }
        >
          <ToastContent>{RETURNING_OWNER_MESSAGE}</ToastContent>
        </ToastContainer>
      )}
    </AnimatePresence>
  );
};

export default KLReturningOwnerToast;
